import { forwardRef, useMemo, type CSSProperties } from 'react';
import { Check } from 'lucide-react';
import { useTheme } from '@course-authoring/shared-types';
import { isUploadedFileSource, type BuildView, type OutlineBlock, type OutlineSnapshotPart } from '../../api/authorWithAiApi';
import { diffCourseLessons, diffLessonBlocks, snapshotTarget, type RowMark } from './outlineDiff';
import { approvalHintItems, assumedFields, formatSectionRange, isCourseApproved, isLessonApproved } from './threadModel';
import { BLOCK_DISPLAY_NAMES, BRIEF_FIELD_LABELS, OUTLINE_COPY } from './authorWithAiCopy';
import { createAuthorWithAiStyles } from './authorWithAiStyles';
import type { LibraryLabel, LibraryRef } from './useLibraryLabels';

/**
 * One outline snapshot as a box: a lesson's numbered blocks (with "Lesson N
 * of M" during lesson-by-lesson review) or a course's lesson list with the
 * total runtime. Rows changed since the previous snapshot of the same target
 * are marked. The latest box also carries the assumptions line, the approval
 * hint and the stale-revision notice. It holds no buttons: approving and
 * starting are chips in the row under the thread.
 */

/** " · slides 1–9" after the minutes of a lesson built from an uploaded file; empty otherwise. */
const sourceRangeSuffix = (view: BuildView, sourceSections: readonly number[] | undefined): string =>
  (isUploadedFileSource(view.source) && sourceSections && sourceSections.length > 0
    ? ` · ${OUTLINE_COPY.sourceRange(view.source.unit, formatSectionRange(sourceSections))}`
    : '');

export interface OutlineBoxProps {
  view: BuildView;
  part: OutlineSnapshotPart;
  previous: OutlineSnapshotPart | null;
  labelFor: (ref: LibraryRef) => LibraryLabel;
  /** Present only on the most recent box: it carries the footer lines. */
  latest?: { staleNotice: boolean };
}

function MarkTag({ mark, styles }: { mark: RowMark; styles: Record<string, CSSProperties> }) {
  if (mark === 'added') return <span style={styles.markTag}>{OUTLINE_COPY.added}</span>;
  if (mark === 'retyped') return <span style={styles.markTag}>{OUTLINE_COPY.retyped}</span>;
  return null;
}

function ApprovedTag({ styles }: { styles: Record<string, CSSProperties> }) {
  return (
    <span style={styles.approvedTag}>
      <Check size="1em" strokeWidth={1.75} aria-hidden="true" />
      {OUTLINE_COPY.approved}
    </span>
  );
}

export const OutlineBox = forwardRef<HTMLDivElement, OutlineBoxProps>(function OutlineBox({ view, part, previous, labelFor, latest }, ref) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createAuthorWithAiStyles(tokens), [tokens]);
  const target = snapshotTarget(part);
  const previousTarget = previous ? snapshotTarget(previous) : null;

  const blockText = (block: OutlineBlock): string => {
    for (const selectionId of block.selectionIds) {
      const selection = view.workingState.selections.find((candidate) => candidate.id === selectionId);
      if (!selection) continue;
      const label = labelFor({ kind: selection.kind, libraryId: selection.libraryId });
      if (label.resolved) return label.label;
    }
    return block.purpose;
  };

  let body;
  if (target.kind === 'lesson') {
    const prior = previousTarget?.kind === 'lesson' ? previousTarget.lesson : null;
    const diff = diffLessonBlocks(prior, target.lesson);
    const approved = isLessonApproved(view, target.lesson.id, target.lesson.revision);
    body = (
      <>
        {target.total > 1 && <span style={styles.outlineHeader}>{OUTLINE_COPY.lessonOf(target.position, target.total)}</span>}
        <div style={styles.outlineTitleRow}>
          <h4 style={styles.outlineTitle}>{target.lesson.title}</h4>
          {approved && <ApprovedTag styles={styles} />}
        </div>
        <ol style={styles.outlineRows}>
          {diff.rows.map(({ item, mark }) => (
            <li key={item.id} style={{ ...styles.outlineRow, ...(mark === 'added' ? styles.outlineRowAdded : {}) }} data-mark={mark ?? undefined}>
              <span style={styles.outlineRowContent}>
                <span>{blockText(item)}<MarkTag mark={mark} styles={styles} /></span>
                <span style={styles.blockName}>{BLOCK_DISPLAY_NAMES[item.type] ?? item.type}</span>
              </span>
            </li>
          ))}
          <li style={styles.outlineRow}>
            <span style={styles.outlineRowContent}>
              <span>{OUTLINE_COPY.wrapUp}</span>
              <span style={styles.blockName}>{OUTLINE_COPY.lessonComplete}</span>
            </span>
          </li>
        </ol>
        {diff.removed.length > 0 && (
          <ul style={styles.removedList}>
            {diff.removed.map((block) => <li key={block.id}>{OUTLINE_COPY.removed(block.purpose)}</li>)}
          </ul>
        )}
        <span style={styles.outlineFooter}>{OUTLINE_COPY.aboutMinutes(target.lesson.estimatedMinutes)}</span>
      </>
    );
  } else {
    const prior = previousTarget?.kind === 'course' ? previousTarget.outline : null;
    const diff = diffCourseLessons(prior, target.outline);
    const total = target.outline.lessons.reduce((sum, lesson) => sum + lesson.estimatedMinutes, 0);
    const courseApproved = isCourseApproved(view, target.outline.id, target.outline.revision);
    body = (
      <>
        <div style={styles.outlineTitleRow}>
          <h4 style={styles.outlineTitle}>{target.outline.title}</h4>
          {courseApproved && <ApprovedTag styles={styles} />}
        </div>
        <ol style={styles.outlineRows}>
          {diff.rows.map(({ item, mark }) => (
            <li key={item.id} style={{ ...styles.outlineRow, ...(mark === 'added' ? styles.outlineRowAdded : {}) }} data-mark={mark ?? undefined}>
              <span style={styles.outlineRowContent}>
                <span>{item.title}<MarkTag mark={mark} styles={styles} /></span>
                <span style={{ display: 'inline-flex', gap: tokens.spacing.sm, alignItems: 'baseline' }}>
                  {isLessonApproved(view, item.id, item.revision) && <ApprovedTag styles={styles} />}
                  <span style={styles.blockName}>{OUTLINE_COPY.minutesShort(item.estimatedMinutes)}{sourceRangeSuffix(view, item.sourceSections)}</span>
                </span>
              </span>
              <span style={{ ...styles.lessonDescription, display: 'block' }}>{item.objectives.join('; ')}</span>
            </li>
          ))}
        </ol>
        {diff.removed.length > 0 && (
          <ul style={styles.removedList}>
            {diff.removed.map((lesson) => <li key={lesson.id}>{OUTLINE_COPY.removed(lesson.title)}</li>)}
          </ul>
        )}
        <span style={styles.outlineFooter}>{OUTLINE_COPY.endToEnd(total)}</span>
      </>
    );
  }

  let footer = null;
  if (latest) {
    const assumed = assumedFields(view).map((field) => BRIEF_FIELD_LABELS[field]);
    const hint = approvalHintItems(view);
    footer = (
      <>
        {assumed.length > 0 && <p style={styles.muted}>{OUTLINE_COPY.assumed(assumed)}</p>}
        {hint.length > 0 && <p style={styles.muted}>{OUTLINE_COPY.needsApproval(hint)}</p>}
        {latest.staleNotice && <p style={styles.error} role="alert">{OUTLINE_COPY.staleRevision}</p>}
      </>
    );
  }

  return (
    <div ref={ref} style={styles.outline} data-testid="outline-box" data-target={target.key}>
      {body}
      {footer}
    </div>
  );
});
